import { API_ENDPOINTS, AppConfigService } from './app-config-service';
import { ANONYMOUS_USER_NAME, UserDetails, UserDetailsService, UserTypeResponse } from './user-details-service';

export type RevalidateStrategy = 'RELOGIN' | 'RELOAD';

export type SessionServiceResponse = {
  isValid: boolean;
  isSignedIn: boolean;
  userName: string;
  revalidationStrategy: RevalidateStrategy;
};

export type SessionCheckOptions = {
  guestUserInvalid: boolean;
  differentUserInvalid: boolean;
  differentCountryInvalid: boolean;
};

export interface SessionService {
  isCurrentSessionValid(options: SessionCheckOptions): ng.IPromise<SessionServiceResponse>;
}

export class SessionServiceImpl implements SessionService {

  private userDetails?: UserDetails;

  constructor(private $q: ng.IQService,
              private userDetailsService: UserDetailsService) {

  }

  /**
   * Compare the user details between when the app is loaded
   * and when this method is called and see if there is any
   * inconsistencies between the two
   * @param options a set of options that dictates the way some user detail information is evaluated
   * @returns boolean true if the user information is still consistent, false otherwise
   */
  public isCurrentSessionValid(options: SessionCheckOptions): ng.IPromise<SessionServiceResponse> {
    if (this.userDetails) {
      const { userName, userId, countryCode } = this.userDetails;
      const { guestUserInvalid, differentUserInvalid, differentCountryInvalid } = options;
      return this.userDetailsService.getUserDetails().then((currentUser) => {
        const { userId: currentUserId, userName: currentUserName, countryCode: currentCountryCode, userType, isGuestUser, isCSR } = currentUser;
        const isCurrentAnonymous = currentUserName === ANONYMOUS_USER_NAME;
        const isSignedIn: boolean = !isCurrentAnonymous;
        const isGuestUserValid: boolean = guestUserInvalid && isGuestUser ? false : true;
        const isAnonymousValid = isCurrentAnonymous === (userName === ANONYMOUS_USER_NAME);
        const isDifferentUser = isSignedIn ? currentUserId !== userId : false;
        const isUserValid = differentUserInvalid && isDifferentUser ? false : true;
        const isDifferentCountry = currentCountryCode ? currentCountryCode !== countryCode : false;
        const isCountryValid = differentCountryInvalid && isDifferentCountry ? false : true;
        const isValid: boolean = isAnonymousValid && isGuestUserValid && isUserValid && isCountryValid;
        const isB2B = userType === 'b2b';
        const revalidationStrategy = (isSignedIn && (isCSR || isB2B)) ? 'RELOAD' : 'RELOGIN';
        return this.$q.resolve<SessionServiceResponse>({ isValid, isSignedIn, userName, revalidationStrategy });
      }, () => {
        const deferred = this.$q.defer<SessionServiceResponse>();
        // if service is down, re-attempt after a specified interval
        setTimeout(() => {
          this.isCurrentSessionValid(options)
            .then(deferred.resolve, deferred.reject);
        }, 5000);
        return deferred.promise;
      });
    } else {
      return this.userDetailsService.getUserDetails().then((userDetails: UserDetails) => {
        this.userDetails = userDetails;
        return this.isCurrentSessionValid(options);
      });
    }
  }
}

export const SESSION_SERVICE_NAME = 'sessionService';
export const SessionServiceFactory = ($q: ng.IQService,
                                      userDetailsService: UserDetailsService): SessionService => {
  return new SessionServiceImpl($q, userDetailsService);
};



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0-0!./src/main/script/service/session-service.ts